
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { 
    PatientContext, 
    InstitutionContext, 
    CollaboratingGroupContext, 
    MasterGroupContext, 
    RouteLoading,
    PageLayout,
    FormBreadcrumbs,
    CrfForm,
    GroupedFields,
    GroupedField,
    ToggleButtonGroup,
    Text,
    Select,
    DatePicker
} from '@ngt/opms';
import React, { useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import { styled, Typography } from '@mui/material';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */
interface IWithdrawalOfConsentProps { }


/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
const StyledSubheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 3, 2)
    
}))

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const WithdrawalOfConsent: React.FunctionComponent<IWithdrawalOfConsentProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            _canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }


    return (
        <>
            {
                <PageLayout
                    breadcrumbs={<FormBreadcrumbs />}
                >
                    <CrfForm
                        validateOn={ValidateOn.onChange}
                        readOnly={true}
                        debug={AppSettings.debug}
                    >
                        <GroupedField
                            propertyPath="dateOfConsentWithdrawal"
                            component={DatePicker}
                        />
                        <GroupedFields>
                            <GroupedField
                                propertyPath="reasonForWithdrawal"
                                component={Select}
                            />
                            <GroupedField
                                propertyPath="reasonForWithdrawalSpecify"
                                component={Text}
                            />
                        </GroupedFields>
                        <GroupedField
                            propertyPath="agreeToBeContactedByTelephone"
                            component={ToggleButtonGroup}
                        />
                        <GroupedField
                            propertyPath="agreeToCollectHealthInfo"
                            component={ToggleButtonGroup}
                        />
                        <GroupedField
                            propertyPath="agreeToUseOfImages"
                            component={ToggleButtonGroup}
                        />
                        <GroupedField
                            propertyPath="agreeToKeepProvidingSamples"
                            component={ToggleButtonGroup}
                        />
                        <GroupedField
                            propertyPath="agreeToKeepCompletingProms"
                            component={ToggleButtonGroup}
                        />
                        <GroupedField
                            propertyPath="agreeToUseServicesAustraliaInfo"
                            component={ToggleButtonGroup}
                        />
                       <StyledSubheading  
                            variant="h2"
                        >
                            Biological Samples
                        </StyledSubheading>
                        <GroupedFields>
                            <GroupedField
                                propertyPath="agreeBiologicalSamples"
                                component={Select}
                            />
                            <GroupedField
                                propertyPath="agreeBiologicalSamplesSpecify"
                                component={Select}
                            />
                        </GroupedFields>
                        <StyledSubheading  
                            variant="h2"
                        >
                            Data Linkage
                        </StyledSubheading>
                        <GroupedFields>
                            <GroupedField
                                propertyPath="agreeDataLinkage"
                                component={Select}
                            />
                            <GroupedField
                                propertyPath="agreeBiologicalSamplesSpecify"
                                component={Select}
                            />
                        </GroupedFields>
                        <StyledSubheading  
                            variant="h2"
                        >
                            Services Australia Participant Withdrawal of Consent Form AUSTRALIA ONLY
                        </StyledSubheading>
                        <GroupedFields>
                            <GroupedField
                                propertyPath="servicesAustraliaWithdrawalOfConsentCompleted"
                                component={ToggleButtonGroup}
                            />
                            <GroupedField
                                propertyPath="indicatedToDestroyOrRetainHealthInfo"
                                component={Select}
                            />
                        </GroupedFields>
                    </CrfForm>
                </PageLayout>
            }
        </>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default WithdrawalOfConsent;
